import InVivoError from '@/model/InVivoError';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { _notNil } from '@/littledash';

type ResolvedValue<V> =
  | { status: 'resolving' }
  | { status: 'fulfilled'; value: V }
  | { status: 'rejected'; reason: InVivoError };

type UseResolvedValueResponse<V, Props extends Record<string, any>> = {
  state: ResolvedValue<V>;
  reload: (props?: Props) => void;
};

export const useResolvedValue = <V, Props extends Record<string, any>>(
  supplier: (props?: Props) => Promise<V>
): UseResolvedValueResponse<V, Props> => {
  const [state, setState] = useState<ResolvedValue<V>>({ status: 'resolving' });
  const id = useId();
  const mounted = useRef(true);
  const supplierRef = useRef(supplier);
  supplierRef.current = supplier;

  const invoke = useCallback(
    async (props?: Props) => {
      const execute = () =>
        supplierRef
          .current(props)
          .then((value) => {
            if (mounted.current) {
              setState({ status: 'fulfilled', value });
            }
          })
          .catch((reason) => {
            if (mounted.current) {
              setState({ status: 'rejected', reason: new InVivoError('Failed to resolve value', { cause: reason }) });
            }
          });

      if (_notNil(navigator.locks)) {
        await navigator.locks.request(
          `use-resolved-value-${id}`,
          { mode: 'exclusive', ifAvailable: true },
          async (lock) => {
            if (_notNil(lock)) {
              await execute();
            }
          }
        );
      } else {
        // Fallback for browsers that do not support the Locks API
        await execute();
      }
    },
    [id, setState, mounted]
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, [mounted]);

  useEffect(() => {
    invoke();
  }, [invoke]);

  return { state, reload: invoke };
};
